<script setup>
// NOTICE: KAPTION-INTEGRATION - WhatsApp Direct Send detail page
// Shows campaign info and usage instructions (curl, Python, JS, LLM)
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
// NOTICE: KAPTION-INTEGRATION - per-wrapper Shorten Links toggle (Direct Send)
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
// NOTICE: KAPTION-INTEGRATION - per-wrapper Card title input (tap_target_configuration)
import Input from 'dashboard/components-next/input/Input.vue';
import { MEDIA_FORMATS } from 'dashboard/helper/templateHelper';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { canUseFeature } = useFeaturePermissions();

if (!canUseFeature('whatsapp_direct_send')) {
  router.replace({ name: 'campaigns_whatsapp_index' });
}

const campaign = ref(null);
const isLoading = ref(true);
const activeSnippetTab = ref('curl');

const accountId = computed(() => route.params.accountId);
const directId = computed(() => route.params.directId);

// Store getter to look up the actual template definition
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

// Fetch campaign details
const fetchCampaign = async () => {
  isLoading.value = true;
  try {
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/direct_messages/${directId.value}`
    );
    campaign.value = response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[KaptionDirect] Failed to fetch campaign detail:', error);
    useAlert(t('DIRECT.DETAIL.FETCH_ERROR'));
    router.push({ name: 'campaigns_direct_index' });
  } finally {
    isLoading.value = false;
  }
};

// Inbox name from store
const inboxName = computed(() => {
  if (!campaign.value?.inbox_id) return '';
  const allInboxes = store.getters['inboxes/getInboxes'] || [];
  const inbox = allInboxes.find(i => i.id === campaign.value.inbox_id);
  return inbox?.name || `Inbox #${campaign.value.inbox_id}`;
});

// Template info from campaign
const templateName = computed(
  () => campaign.value?.template_params?.name || ''
);
const templateLanguage = computed(
  () => campaign.value?.template_params?.language || ''
);

// Look up the real template definition from inbox templates
const templateDefinition = computed(() => {
  if (!campaign.value?.inbox_id || !templateName.value) return null;
  const templates =
    getFilteredWhatsAppTemplates.value(campaign.value.inbox_id) || [];
  return templates.find(
    tmpl =>
      tmpl.name === templateName.value &&
      tmpl.language === templateLanguage.value
  );
});

// Extract variable names + example values from template body
const templateVariableExamples = computed(() => {
  if (!templateDefinition.value) return {};
  const body = templateDefinition.value.components?.find(
    c => c.type === 'BODY'
  );
  const namedParams = body?.example?.body_text_named_params;
  if (namedParams?.length) {
    const result = {};
    namedParams.forEach(p => {
      result[p.param_name] = p.example || `Sample_${p.param_name}`;
    });
    return result;
  }
  // Fallback: extract {{N}} positional from body text
  const text = body?.text || '';
  const matches = Array.from(text.matchAll(/{{\s*([^}]+)\s*}}/g));
  if (matches.length === 0) return {};
  const result = {};
  matches.forEach(m => {
    const key = m[1].trim();
    result[key] = `Sample_${key}`;
  });
  return result;
});

// Does this template have variables?
const hasVariables = computed(
  () => Object.keys(templateVariableExamples.value).length > 0
);

// ---------- Header media detection ----------
const headerComponent = computed(() => {
  if (!templateDefinition.value) return null;
  return templateDefinition.value.components?.find(c => c.type === 'HEADER');
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

// NOTICE: KAPTION-INTEGRATION — Detect a URL button in the wrapper's template.
// The tappable card title (tap_target_configuration) only has an effect when a
// tracked kapt.io URL button exists, so the input is gated on this.
const hasUrlButton = computed(() => {
  if (!templateDefinition.value) return false;
  const buttonsComp = templateDefinition.value.components?.find(
    c => c.type === 'BUTTONS'
  );
  if (!buttonsComp) return false;
  return (buttonsComp.buttons || []).some(b => b.type === 'URL');
});

const mediaHeaderType = computed(
  () => headerComponent.value?.format?.toLowerCase() || ''
);

const templateExampleMediaUrl = computed(() => {
  const hdr = headerComponent.value;
  if (!hdr) return '';
  return (
    hdr.example?.header_handle?.[0] ||
    hdr.example?.header_url?.[0] ||
    hdr.example?.[0] ||
    ''
  );
});

const MEDIA_ICON_MAP = {
  image: 'i-lucide-image',
  video: 'i-lucide-film',
  document: 'i-lucide-file-text',
};

const mediaHeaderIcon = computed(
  () => MEDIA_ICON_MAP[mediaHeaderType.value] || 'i-lucide-file-text'
);

const mediaHeaderLabel = computed(() =>
  t('DIRECT.DETAIL.ATTACHMENT_BADGE', { type: mediaHeaderType.value })
);

// Campaign-stored header media (from processed_params defaults)
const campaignHeaderMedia = computed(() => {
  const pp = campaign.value?.template_params?.processed_params;
  if (!pp?.header?.media_url) return null;
  return pp.header;
});

// Placeholder URL for the media type (used in example snippets)
const mediaPlaceholderUrl = computed(() => {
  if (campaignHeaderMedia.value?.media_url)
    return campaignHeaderMedia.value.media_url;
  if (templateExampleMediaUrl.value) return templateExampleMediaUrl.value;
  const ext = { image: 'jpg', video: 'mp4', document: 'pdf' };
  return `https://example.com/your-file.${ext[mediaHeaderType.value] || 'file'}`;
});

// Build the processed_params example using real variable names + header media
const processedParamsExample = computed(() => {
  const result = {};
  const vars = templateVariableExamples.value;
  if (Object.keys(vars).length > 0) {
    result.body = vars;
  }
  if (hasMediaHeader.value) {
    result.header = {
      media_url: mediaPlaceholderUrl.value,
      media_type: mediaHeaderType.value,
    };
  }
  return Object.keys(result).length > 0 ? result : null;
});

// Build the payload example for snippets
const payloadExample = computed(() => {
  const payload = { phone_number: '+1234567890' };
  if (processedParamsExample.value) {
    payload.processed_params = processedParamsExample.value;
  }
  return payload;
});

// Base URL for API
const baseUrl = computed(() => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'https://chat.kaptionai.com';
});

const apiPath = computed(
  () =>
    `/api/v1/accounts/${accountId.value}/direct_messages/${directId.value}/send_message`
);

// HTTP method label (avoids bare-string lint)
const httpMethod = computed(() => 'POST');

// ---------- Code snippet generators ----------

// NOTICE: KAPTION-INTEGRATION
// Each snippet shows two approaches when a media header is required:
// 1. JSON with a public URL (media_url)
// 2. Multipart form with direct file upload (header_media)

const curlSnippet = computed(() => {
  const paramsJson = JSON.stringify(payloadExample.value, null, 2);

  let snippet = `# Option 1: JSON with media URL
curl -X POST '${baseUrl.value}${apiPath.value}' \\
  -H 'Content-Type: application/json' \\
  -H 'api_access_token: YOUR_API_TOKEN' \\
  -d '${paramsJson}'`;

  if (hasMediaHeader.value) {
    const extMap = { document: 'pdf', video: 'mp4', image: 'jpg' };
    const ext = extMap[mediaHeaderType.value] || 'file';
    const formFields = [`  -F 'phone_number=+1234567890'`];
    formFields.push(`  -F 'header_media=@/path/to/your-file.${ext}'`);
    const vars = templateVariableExamples.value;
    Object.entries(vars).forEach(([k, v]) => {
      formFields.push(`  -F 'processed_params[body][${k}]=${v}'`);
    });

    snippet += `\n\n# Option 2: Direct file upload (multipart)
curl -X POST '${baseUrl.value}${apiPath.value}' \\
  -H 'api_access_token: YOUR_API_TOKEN' \\
${formFields.join(' \\\n')}`;
  }

  return snippet;
});

const pythonSnippet = computed(() => {
  const paramsJson = JSON.stringify(payloadExample.value, null, 4);

  let snippet = `import requests

url = "${baseUrl.value}${apiPath.value}"
headers = {
    "Content-Type": "application/json",
    "api_access_token": "YOUR_API_TOKEN"
}
payload = ${paramsJson}

# Option 1: JSON with media URL
response = requests.post(url, json=payload, headers=headers)
print(response.json())`;

  if (hasMediaHeader.value) {
    const pyExtMap = { document: 'pdf', video: 'mp4', image: 'jpg' };
    const ext = pyExtMap[mediaHeaderType.value] || 'file';
    const varEntries = Object.entries(templateVariableExamples.value);
    const bodyParams = varEntries
      .map(([k, v]) => `    ("processed_params[body][${k}]", "${v}"),`)
      .join('\n');

    snippet += `

# Option 2: Direct file upload (multipart)
headers_upload = {"api_access_token": "YOUR_API_TOKEN"}
with open("your-file.${ext}", "rb") as f:
    files = {"header_media": f}
    data = [
        ("phone_number", "+1234567890"),
${bodyParams}
    ]
    response = requests.post(url, headers=headers_upload, data=data, files=files)
    print(response.json())`;
  }

  return snippet;
});

const jsSnippet = computed(() => {
  const paramsJson = JSON.stringify(payloadExample.value, null, 2);

  let snippet = `// Option 1: JSON with media URL
const response = await fetch('${baseUrl.value}${apiPath.value}', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'api_access_token': 'YOUR_API_TOKEN',
  },
  body: JSON.stringify(${paramsJson}),
});

const data = await response.json();
console.log(data);`;

  if (hasMediaHeader.value) {
    const varEntries = Object.entries(templateVariableExamples.value);
    const appendLines = varEntries
      .map(
        ([k, v]) => `formData.append('processed_params[body][${k}]', '${v}');`
      )
      .join('\n');

    snippet += `

// Option 2: Direct file upload (multipart)
const formData = new FormData();
formData.append('phone_number', '+1234567890');
formData.append('header_media', fileInput.files[0]);
${appendLines}

const uploadResponse = await fetch('${baseUrl.value}${apiPath.value}', {
  method: 'POST',
  headers: { 'api_access_token': 'YOUR_API_TOKEN' },
  body: formData,
});

console.log(await uploadResponse.json());`;
  }

  return snippet;
});

const llmSnippet = computed(() => {
  const paramParts = [];

  if (hasMediaHeader.value) {
    paramParts.push(`  - processed_params.header: Object with media attachment (REQUIRED for this template)
    - media_url: Public URL of the ${mediaHeaderType.value} file (must be HTTPS, max 15 MB)
    - media_type: "${mediaHeaderType.value}"`);
  }

  if (hasVariables.value) {
    const varList = Object.entries(templateVariableExamples.value)
      .map(([k, v]) => `    - ${k}: e.g. "${v}"`)
      .join('\n');
    paramParts.push(`  - processed_params.body: Object mapping variable names to values
${varList}`);
  }

  let paramSection = '';
  if (paramParts.length > 0) {
    paramSection = `  - processed_params: Object with template parameters
${paramParts.join('\n')}`;
  } else {
    paramSection =
      '  - processed_params: (not needed — this template has no variables or media)';
  }

  const exampleBody = JSON.stringify(payloadExample.value, null, 2);

  let attachmentNote = '';
  if (hasMediaHeader.value) {
    const formatMap = {
      image: 'JPG, PNG, WebP',
      video: 'MP4',
      document: 'PDF',
    };
    const formats = formatMap[mediaHeaderType.value] || mediaHeaderType.value;
    attachmentNote = `\n\n### Attachment\nThis template requires a **${mediaHeaderType.value}** attachment. Two options:\n1. **URL**: Provide a publicly accessible HTTPS URL in \`processed_params.header.media_url\` (JSON request)\n2. **File upload**: Send a multipart request with the file in the \`header_media\` field (no \`Content-Type: application/json\` header — let the client set multipart boundaries)\n\nSupported formats: ${formats}. Max 15 MB.`;
  }

  return `## Direct Send API — "${campaign.value?.title || templateName.value}"

Send a WhatsApp template message to a single phone number using this pre-configured campaign.

**Template:** ${templateName.value} (${templateLanguage.value})
**Inbox:** ${inboxName.value}${hasMediaHeader.value ? `\n**Attachment:** ${mediaHeaderType.value.charAt(0).toUpperCase() + mediaHeaderType.value.slice(1)} header (required)` : ''}

### Endpoint
POST ${baseUrl.value}${apiPath.value}

### Authentication
Include header: api_access_token: YOUR_API_TOKEN

### Required Parameters
- phone_number: Recipient phone number with country code (e.g. "+1234567890")${hasMediaHeader.value ? `\n- processed_params.header.media_url: Public URL of the ${mediaHeaderType.value} file` : ''}

### Optional Parameters
${paramSection}
  - scheduled_at: ISO 8601 datetime for scheduled delivery (e.g. "2025-12-01T10:00:00Z")
  - create_conversation: Boolean, creates a conversation record (default: false)
  - metadata: Object with custom metadata for tracking${attachmentNote}

### Example
${exampleBody}

### Response
{ "success": true, "tracking_id": "kp_...", "direct_id": ${directId.value}, "scheduled": false }`;
});

const snippetTabs = computed(() => [
  { label: 'curl', value: 'curl' },
  { label: 'Python', value: 'python' },
  { label: 'JavaScript', value: 'js' },
  { label: t('DIRECT.DETAIL.LLM_TAB'), value: 'llm' },
]);

const activeSnippet = computed(() => {
  switch (activeSnippetTab.value) {
    case 'curl':
      return curlSnippet.value;
    case 'python':
      return pythonSnippet.value;
    case 'js':
      return jsSnippet.value;
    case 'llm':
      return llmSnippet.value;
    default:
      return curlSnippet.value;
  }
});

const handleSnippetTabChange = tab => {
  activeSnippetTab.value = tab.value;
};

const snippetTabIndex = computed(() => {
  const tabs = ['curl', 'python', 'js', 'llm'];
  return tabs.indexOf(activeSnippetTab.value);
});

// Copy to clipboard
const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(activeSnippet.value);
    useAlert(t('DIRECT.DETAIL.COPIED'));
  } catch {
    useAlert(t('DIRECT.DETAIL.COPY_FAILED'));
  }
};

// Navigate to send form with locked campaign context
const navigateToSend = () => {
  router.push({
    name: 'campaigns_direct_send',
    query: { directId: directId.value },
  });
};

// Navigate to report page
const navigateToReport = () => {
  router.push({
    name: 'campaigns_direct_report',
    params: { directId: directId.value },
  });
};

const handleBack = () => {
  router.push({ name: 'campaigns_direct_index' });
};

onMounted(() => {
  if (!canUseFeature('whatsapp_direct_send')) return;
  store.dispatch('inboxes/get');
  fetchCampaign();
});

// NOTICE: KAPTION-INTEGRATION
// Refetch when navigating between DM detail pages (Vue reuses the component)
// NOTICE: KAPTION-INTEGRATION — guard against undefined directId during route transitions
// (e.g., navigating from direct/:directId to direct/new clears the param)
watch(directId, newVal => {
  if (newVal && canUseFeature('whatsapp_direct_send')) fetchCampaign();
});

// NOTICE: KAPTION-INTEGRATION — Per-wrapper Shorten Links toggle.
// Storage is tri-state in `trigger_rules.shorten_links`: true / false /
// missing (= "not set, use account default"). The Switch UI is binary;
// we display `null` as off and let an explicit toggle persist `true` or
// `false` to the jsonb column. PATCH goes through the standard
// /campaigns endpoint; the KaptionCampaignTriggerRules model concern
// normalizes booleans server-side.
const shortenLinks = ref(false);
const isSavingShortenLinks = ref(false);

const syncShortenLinksFromCampaign = () => {
  const raw = campaign.value?.trigger_rules?.shorten_links;
  shortenLinks.value = raw === true;
};

watch(campaign, syncShortenLinksFromCampaign, { immediate: true });

const saveShortenLinks = async newValue => {
  if (!campaign.value) return;
  // Preserve every other key in trigger_rules — we only own `shorten_links`.
  const existing = { ...(campaign.value.trigger_rules || {}) };
  existing.shorten_links = newValue;

  isSavingShortenLinks.value = true;
  try {
    const response = await window.axios.patch(
      `/api/v1/accounts/${accountId.value}/campaigns/${campaign.value.display_id}`,
      { trigger_rules: existing }
    );
    // Re-sync local campaign state from the response so the UI reflects
    // any server-side coercion (e.g. "false" → false via the concern).
    if (response?.data) {
      campaign.value = { ...campaign.value, ...response.data };
    } else {
      campaign.value = { ...campaign.value, trigger_rules: existing };
    }
    shortenLinks.value = newValue === true;
    useAlert(t('DIRECT.DETAIL.SHORTEN_LINKS_SAVED'));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[KaptionDirect] Failed to save shorten_links:', error);
    useAlert(t('DIRECT.DETAIL.SHORTEN_LINKS_SAVE_ERROR'));
    // Revert the local toggle so the UI matches persisted state.
    syncShortenLinksFromCampaign();
  } finally {
    isSavingShortenLinks.value = false;
  }
};

// NOTICE: KAPTION-INTEGRATION — Per-wrapper DRY-RUN. Same PATCH-to-/campaigns pattern as
// shorten_links. When on, every send through this wrapper is simulated (a `send_attempt`
// event, no Meta delivery), so it can be unarchived/tested without reaching real customers.
const dryRun = ref(false);
const isSavingDryRun = ref(false);

const syncDryRunFromCampaign = () => {
  dryRun.value = campaign.value?.trigger_rules?.dry_run === true;
};

watch(campaign, syncDryRunFromCampaign, { immediate: true });

const saveDryRun = async newValue => {
  if (!campaign.value) return;
  // Preserve every other key in trigger_rules — we only own `dry_run`.
  const existing = { ...(campaign.value.trigger_rules || {}) };
  existing.dry_run = newValue;

  isSavingDryRun.value = true;
  try {
    const response = await window.axios.patch(
      `/api/v1/accounts/${accountId.value}/campaigns/${campaign.value.display_id}`,
      { trigger_rules: existing }
    );
    if (response?.data) {
      campaign.value = { ...campaign.value, ...response.data };
    } else {
      campaign.value = { ...campaign.value, trigger_rules: existing };
    }
    dryRun.value = newValue === true;
    useAlert(t('DIRECT.DETAIL.DRY_RUN_SAVED'));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[KaptionDirect] Failed to save dry_run:', error);
    useAlert(t('DIRECT.DETAIL.DRY_RUN_SAVE_ERROR'));
    // Revert the local toggle so the UI matches persisted state.
    syncDryRunFromCampaign();
  } finally {
    isSavingDryRun.value = false;
  }
};

// NOTICE: KAPTION-INTEGRATION — Per-wrapper Card title (tap_target_configuration).
// Stored in `trigger_rules.tap_target_title`; applied to every send through this
// wrapper when the template has a tracked kapt.io URL button. Mirrors the
// shorten_links persistence exactly — PATCH the standard /campaigns endpoint and
// preserve every other trigger_rules key.
const tapTargetTitle = ref('');
const isSavingTapTargetTitle = ref(false);

const syncTapTargetTitleFromCampaign = () => {
  tapTargetTitle.value = campaign.value?.trigger_rules?.tap_target_title || '';
};

watch(campaign, syncTapTargetTitleFromCampaign, { immediate: true });

const saveTapTargetTitle = async () => {
  if (!campaign.value) return;
  const newValue = tapTargetTitle.value?.trim() || null;
  // Preserve every other key in trigger_rules — we only own `tap_target_title`.
  const existing = { ...(campaign.value.trigger_rules || {}) };
  existing.tap_target_title = newValue;

  isSavingTapTargetTitle.value = true;
  try {
    const response = await window.axios.patch(
      `/api/v1/accounts/${accountId.value}/campaigns/${campaign.value.display_id}`,
      { trigger_rules: existing }
    );
    if (response?.data) {
      campaign.value = { ...campaign.value, ...response.data };
    } else {
      campaign.value = { ...campaign.value, trigger_rules: existing };
    }
    tapTargetTitle.value = newValue || '';
    useAlert(t('DIRECT.DETAIL.SHORTEN_LINKS_SAVED'));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[KaptionDirect] Failed to save tap_target_title:', error);
    useAlert(t('DIRECT.DETAIL.SHORTEN_LINKS_SAVE_ERROR'));
    // Revert the local input so the UI matches persisted state.
    syncTapTargetTitleFromCampaign();
  } finally {
    isSavingTapTargetTitle.value = false;
  }
};
</script>

<template>
  <div
    class="wp-card wp-campaign-fullpage flex flex-col h-full bg-n-background -mx-6"
  >
    <!-- Header -->
    <header
      class="relative flex items-center px-8 py-4 border-b border-n-weak bg-n-solid-2"
    >
      <button
        type="button"
        class="flex items-center gap-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
        @click="handleBack"
      >
        <span class="i-lucide-arrow-left w-5 h-5" />
        <span class="text-sm font-medium">{{ t('DIRECT.SEND.BACK') }}</span>
      </button>
      <h1
        class="absolute left-1/2 -translate-x-1/2 text-lg font-semibold text-n-slate-12"
      >
        {{ t('DIRECT.DETAIL.TITLE') }}
      </h1>
    </header>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <Spinner />
    </div>

    <!-- Content -->
    <main v-else-if="campaign" class="flex-1 overflow-y-auto px-8 py-6">
      <div class="max-w-4xl mx-auto flex flex-col gap-8">
        <!-- Campaign Info Card -->
        <div
          class="flex items-start justify-between p-6 rounded-xl border border-n-weak bg-n-alpha-black2"
        >
          <div class="flex flex-col gap-3">
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ campaign.title }}
            </h2>
            <div class="flex flex-wrap items-center gap-4 text-sm">
              <div class="flex items-center gap-1.5 text-n-slate-11">
                <span class="i-lucide-file-text w-4 h-4 text-n-slate-9" />
                <span>{{ `${templateName} (${templateLanguage})` }}</span>
              </div>
              <div class="flex items-center gap-1.5 text-n-slate-11">
                <span class="i-lucide-inbox w-4 h-4 text-n-slate-9" />
                <span>{{ inboxName }}</span>
              </div>
              <div class="flex items-center gap-1.5 text-n-slate-11">
                <span class="i-lucide-calendar w-4 h-4 text-n-slate-9" />
                <span>{{
                  new Date(campaign.created_at).toLocaleDateString()
                }}</span>
              </div>
              <div
                v-if="hasMediaHeader"
                class="flex items-center gap-1.5 text-n-slate-11"
              >
                <span
                  class="w-4 h-4 text-n-slate-9"
                  :class="[mediaHeaderIcon]"
                />
                <span>{{ mediaHeaderLabel }}</span>
              </div>
            </div>
            <span
              class="self-start px-2 py-0.5 text-xs rounded-full"
              :class="
                campaign.title?.startsWith('[Default]')
                  ? 'bg-n-alpha-black4 text-n-slate-10'
                  : 'bg-kpt-amber/10 text-kpt-amber'
              "
            >
              {{
                campaign.title?.startsWith('[Default]')
                  ? t('DIRECT.AUTO')
                  : t('DIRECT.CUSTOM')
              }}
            </span>
            <!-- NOTICE: KAPTION-INTEGRATION - DRY-RUN badge (sends simulated, no delivery) -->
            <span
              v-if="campaign.trigger_rules?.dry_run"
              class="self-start px-2 py-0.5 text-xs font-medium rounded-full bg-n-blue-3 text-n-blue-11"
            >
              {{ t('DIRECT.DRY_RUN') }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <Button
              :label="t('DIRECT.REPORT.TITLE')"
              icon="i-lucide-bar-chart-2"
              size="sm"
              variant="hollow"
              @click="navigateToReport"
            />
            <Button
              :label="t('DIRECT.SEND.SEND_BUTTON')"
              icon="i-lucide-send"
              size="sm"
              @click="navigateToSend"
            />
          </div>
        </div>

        <!-- API Endpoint -->
        <div class="flex flex-col gap-2">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('DIRECT.DETAIL.ENDPOINT_LABEL') }}
          </h3>
          <div
            class="flex items-center gap-2 p-3 rounded-lg border border-n-weak bg-n-alpha-black2 font-mono text-sm text-n-slate-11"
          >
            <span
              class="px-2 py-0.5 rounded text-xs font-bold bg-kpt-amber/15 text-kpt-amber"
              >{{ httpMethod }}</span
            >
            <span class="flex-1 truncate select-all">{{
              `${baseUrl}${apiPath}`
            }}</span>
          </div>
        </div>

        <!-- Code Snippets -->
        <div class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('DIRECT.DETAIL.SNIPPETS_LABEL') }}
            </h3>
            <div class="flex items-center gap-2">
              <TabBar
                :tabs="snippetTabs"
                :initial-active-tab="snippetTabIndex"
                @tab-changed="handleSnippetTabChange"
              />
            </div>
          </div>

          <div class="relative">
            <button
              type="button"
              class="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-n-slate-11 bg-n-alpha-black4 hover:bg-n-alpha-black6 hover:text-n-slate-12 transition-colors"
              @click="copyToClipboard"
            >
              <span class="i-lucide-copy w-3.5 h-3.5" />
              {{ t('DIRECT.DETAIL.COPY') }}
            </button>
            <pre
              class="p-4 pt-12 rounded-xl border border-n-weak bg-n-solid-1 text-sm text-n-slate-11 overflow-x-auto whitespace-pre-wrap break-words font-mono leading-relaxed"
              >{{ activeSnippet }}</pre
            >
          </div>
        </div>

        <!-- Notes -->
        <div
          class="flex items-start gap-3 p-4 rounded-xl border border-n-weak bg-n-alpha-black2"
        >
          <span class="i-lucide-info w-5 h-5 text-n-slate-9 mt-0.5 shrink-0" />
          <div class="flex flex-col gap-1 text-sm text-n-slate-11">
            <p>{{ t('DIRECT.DETAIL.NOTE_TOKEN') }}</p>
            <p>{{ t('DIRECT.DETAIL.NOTE_PHONE') }}</p>
            <p v-if="hasMediaHeader">
              {{ t('DIRECT.DETAIL.NOTE_MEDIA', { type: mediaHeaderType }) }}
            </p>
          </div>
        </div>

        <!-- NOTICE: KAPTION-INTEGRATION — Wrapper settings (per-Direct preferences) -->
        <div class="flex flex-col gap-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('DIRECT.DETAIL.SETTINGS_TITLE') }}
          </h3>
          <div
            class="flex items-start justify-between gap-4 p-4 rounded-xl border border-n-weak bg-n-alpha-black2"
          >
            <div class="flex flex-col gap-1 flex-1 min-w-0">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('DIRECT.DETAIL.SHORTEN_LINKS_LABEL') }}
              </span>
              <p class="text-xs text-n-slate-11">
                {{ t('DIRECT.DETAIL.SHORTEN_LINKS_HINT') }}
              </p>
            </div>
            <div class="shrink-0 mt-0.5">
              <!-- Switch.vue mutates v-model first, so by the time @change
                   fires, `shortenLinks` already holds the new boolean.
                   We pass that authoritative value (not the @change arg). -->
              <ToggleSwitch
                v-model="shortenLinks"
                @change="() => saveShortenLinks(shortenLinks)"
              />
            </div>
          </div>

          <!-- NOTICE: KAPTION-INTEGRATION — DRY-RUN: simulate sends (no delivery) -->
          <div
            class="flex items-start justify-between gap-4 p-4 rounded-xl border border-n-weak bg-n-alpha-black2"
          >
            <div class="flex flex-col gap-1 flex-1 min-w-0">
              <span
                class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
              >
                {{ t('DIRECT.DETAIL.DRY_RUN_LABEL') }}
                <span
                  v-if="dryRun"
                  class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-blue-3 text-n-blue-11"
                >
                  {{ t('DIRECT.DRY_RUN') }}
                </span>
              </span>
              <p class="text-xs text-n-slate-11">
                {{ t('DIRECT.DETAIL.DRY_RUN_HINT') }}
              </p>
            </div>
            <div class="shrink-0 mt-0.5">
              <ToggleSwitch
                v-model="dryRun"
                @change="() => saveDryRun(dryRun)"
              />
            </div>
          </div>

          <!-- NOTICE: KAPTION-INTEGRATION — Card title (tap_target_configuration).
               Only shown when the wrapper's template has a tracked kapt.io URL
               button, since the backend no-ops otherwise. Saved on blur. -->
          <div
            v-if="hasUrlButton"
            class="flex flex-col gap-2 p-4 rounded-xl border border-n-weak bg-n-alpha-black2"
          >
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('DIRECT.DETAIL.TAP_TARGET_TITLE_LABEL') }}
              </span>
              <p class="text-xs text-n-slate-11">
                {{ t('DIRECT.DETAIL.TAP_TARGET_TITLE_HINT') }}
              </p>
            </div>
            <Input
              v-model="tapTargetTitle"
              type="text"
              :placeholder="t('DIRECT.DETAIL.TAP_TARGET_TITLE_PLACEHOLDER')"
              :disabled="isSavingTapTargetTitle"
              @blur="saveTapTargetTitle"
            />
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
